import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const API_BASE = (import.meta.env.VITE_SUPABASE_URL ?? 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');

export interface ChamadoCarteira {
  id: string;
  created_at: string;
  status: string;
  texto: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  autor_nome: string | null;
}

async function comToken() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sessão expirada. Faça login novamente.');
  return token;
}

interface RespostaChamado {
  data?: { aviso_id?: string; movimento_id?: string; destino?: string } | ChamadoCarteira[];
  error?: string;
}

async function ler(response: Response): Promise<RespostaChamado | null> {
  const texto = await response.text();
  let payload: RespostaChamado | null = null;
  try { payload = texto ? (JSON.parse(texto) as RespostaChamado) : null; } catch { payload = null; }
  if (!response.ok) throw new Error(payload?.error || 'Não foi possível falar com o CRM.');
  return payload;
}

/** Chamados ABERTOS deste CNPJ — o que a timeline mostra em cima. */
export function useChamadosCarteira(cnpj: string | null | undefined) {
  const digitos = (cnpj || '').replace(/\D/g, '');
  return useQuery({
    queryKey: ['carteira-chamados', digitos],
    enabled: digitos.length === 14,
    staleTime: 30_000,
    queryFn: async (): Promise<ChamadoCarteira[]> => {
      const token = await comToken();
      const response = await fetch(`${API_BASE}/v1/carteira/chamados?cnpj=${digitos}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await ler(response);
      return (Array.isArray(payload?.data) ? payload.data : []) as ChamadoCarteira[];
    },
  });
}

/** Abre um chamado: grava na timeline do CNPJ e avisa quem vai atender. */
export function useAbrirChamadoCarteira(cnpj: string | null | undefined) {
  const digitos = (cnpj || '').replace(/\D/g, '');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { destino_usuario_id: string; texto: string; cliente_id?: string | null }) => {
      const token = await comToken();
      const response = await fetch(`${API_BASE}/v1/carteira/chamados`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ ...input, cnpj: digitos }),
      });
      return ler(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carteira-chamados', digitos] });
      queryClient.invalidateQueries({ queryKey: ['timeline-unificada', digitos] });
    },
  });
}

/** Responder É resolver: grava a resposta na timeline e encerra o aviso. */
export function useResponderChamadoCarteira(cnpj: string | null | undefined) {
  const digitos = (cnpj || '').replace(/\D/g, '');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { chamadoId: string; texto: string }) => {
      const token = await comToken();
      const response = await fetch(`${API_BASE}/v1/carteira/chamados/${input.chamadoId}/responder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ texto: input.texto }),
      });
      return ler(response);
    },
    onSuccess: () => {
      // O aviso some sozinho da tela de quem respondeu: `useAvisosUsuario`
      // ouve `avisos` por realtime, não por react-query.
      queryClient.invalidateQueries({ queryKey: ['carteira-chamados', digitos] });
      queryClient.invalidateQueries({ queryKey: ['timeline-unificada', digitos] });
    },
  });
}
